import { useParams, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { AlertTriangle, ChevronRight, FileText, MessageCircle, Scale, ArrowRight, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { DisclaimerBanner } from '@/components/ui/DisclaimerBanner';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';

// Category content data
const categoryContent: Record<string, {
  title: string;
  description: string;
  keywords: string[];
  quickAnswer: string;
  sections: Array<{
    id: string;
    title: string;
    content: string;
  }>;
  faqs: Array<{
    question: string;
    answer: string;
  }>;
  relatedTopics: Array<{
    title: string;
    url: string;
    description: string;
  }>;
}> = {
  'landlord-tenant-rights': {
    title: 'Landlord & Tenant Rights',
    description: 'Understand your rights as a tenant or landlord. Learn about security deposits, eviction laws, repair obligations, and more.',
    keywords: ['tenant rights', 'landlord obligations', 'security deposit', 'eviction', 'rent increase', 'habitable conditions'],
    quickAnswer: 'Tenants have the right to a habitable living environment, privacy, and protection from unlawful eviction. Landlords must maintain the property, provide essential services, and follow legal procedures for any deductions or terminations.',
    sections: [
      {
        id: 'security-deposits',
        title: 'Security Deposits',
        content: `Security deposits are funds tenants pay to landlords as protection against damage or unpaid rent. Most states limit the amount a landlord can charge (often 1-2 months\' rent) and require specific procedures for returning the deposit.

**Key rights regarding security deposits:**
- Landlords must provide a written receipt for the deposit
- Many states require the deposit to be held in a specific type of account
- Landlords must return the deposit within a specific timeframe (usually 14-30 days)
- They must provide an itemized list of deductions if money is withheld
- Normal wear and tear cannot be deducted

**What landlords CAN deduct for:**
- Unpaid rent
- Damage beyond normal wear and tear
- Cleaning costs beyond normal cleaning
- Unreturned keys
- Lease violations that resulted in damage`
      },
      {
        id: 'eviction-protections',
        title: 'Eviction Protections',
        content: `Eviction laws vary by state but generally require landlords to follow specific legal procedures. Self-help eviction (changing locks, turning off utilities, removing belongings) is illegal in most jurisdictions.

**Typical eviction process:**
1. Landlord must provide written notice (pay or quit, cure or quit, or unconditional quit)
2. Timeframe to respond varies by state and reason
3. If tenant doesn\'t comply, landlord files eviction lawsuit
4. Court issues summons and sets hearing date
5. Judge rules and may issue writ of possession if landlord wins

**Eviction protections to know:**
- Cannot evict in retaliation for complaining about code violations
- Cannot discriminate under Fair Housing Act
- Some cities have rent control or just cause eviction requirements
- COVID-19 emergency protections may still apply in some areas`
      },
      {
        id: 'repair-and-maintenance',
        title: 'Repair and Maintenance',
        content: `Landlords are generally required to maintain "habitable" living conditions under the implied warranty of habitability. This means providing:

**Essential services:**
- Working plumbing and heating
- Hot and cold running water
- Electricity and gas
- Weatherproof structure
- Sanitary conditions

**Tenant rights:**
- To request repairs in writing
- To have repairs completed in a reasonable timeframe
- To withhold rent or repair and deduct (in some states) if conditions are severe
- To report violations to housing authorities

**Important:** Keep records of all repair requests and communications with your landlord.`
      },
      {
        id: 'privacy-and-entry',
        title: 'Privacy and Entry Rights',
        content: `Tenants have a right to quiet enjoyment of their rental unit. Landlords generally must provide advance notice before entering, except in emergencies.

**Typical notice requirements:**
- Most states require 24-48 hours notice
- Notice must be in writing in many jurisdictions
- Entry should be during reasonable hours (typically 8am-9pm)

**When landlords CAN enter without notice:**
- Emergencies (fire, flood, burst pipe)
- Suspected abandonment
- To show unit to prospective renters or buyers (with notice)

**Best practices:**
- Check your lease for specific terms
- Document any unauthorized entries
- Know your state\'s specific laws`
      },
    ],
    faqs: [
      {
        question: 'Can my landlord keep my security deposit?',
        answer: "Landlords can only keep your security deposit for unpaid rent, damage beyond normal wear and tear, or cleaning costs to return the unit to its original condition. They must provide an itemized list of deductions and return any remaining balance within your state's required timeframe (usually 14-30 days)."
      },
      {
        question: 'How much notice does my landlord have to give before entering my apartment?',
        answer: 'Notice requirements vary by state but typically range from 24-48 hours. Most states require written notice for non-emergency entry. Landlords can only enter without notice in true emergencies or if they have reason to believe you\'ve abandoned the unit.'
      },
      {
        question: 'Can my landlord raise my rent whenever they want?',
        answer: "In states without rent control, landlords can generally raise rent as long as it's done according to your lease terms and with proper notice (usually 30-60 days). Some states require just cause for evictions but not rent increases. Check local laws for specific protections."
      },
      {
        question: 'What should I do if my landlord won\'t make repairs?',
        answer: 'Document the issue in writing, keep copies of all requests, and set a reasonable deadline. If repairs aren\'t made, you may have options like contacting local housing inspectors, withholding rent (in some states), making repairs and deducting from rent, or filing in housing court.'
      },
      {
        question: 'Can my landlord evict me without going to court?',
        answer: 'No. Landlords must follow the legal eviction process in all states, which includes filing an eviction lawsuit and getting a court order. Self-help eviction tactics like changing locks, removing belongings, or shutting off utilities are illegal and can result in the landlord being sued.'
      },
    ],
    relatedTopics: [
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Sue for security deposit return' },
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Lease agreement basics' },
      { title: 'Consumer Rights', url: '/legal/consumer-rights', description: 'Understanding your rights' },
    ]
  },
  'employee-rights': {
    title: 'Employment Law & Employee Rights',
    description: 'Know your rights in the workplace. Learn about wrongful termination, wage laws, discrimination protections, and creating a safe work environment.',
    keywords: ['employee rights', 'wrongful termination', 'wage and hour', 'workplace discrimination', 'sexual harassment', 'FMLA', 'ADA'],
    quickAnswer: 'Employees have protections against discrimination, right to minimum wage and overtime, and safe working conditions. At-will employment means you can be fired for any reason (except illegal ones), but you have recourse if your rights are violated.',
    sections: [
      {
        id: 'at-will-employment',
        title: 'Understanding At-Will Employment',
        content: `At-will employment is the default in most states, meaning either employer or employee can end the employment relationship at any time, for any reason (or no reason), with or without notice.

**However, at-will employment does NOT mean:**
- You can be fired for discriminatory reasons
- You can be fired for reporting illegal activity (whistleblower protection)
- You can be fired for refusing to do something illegal
- You can be fired for exercising your legal rights (voting, jury duty)

**Common exceptions to at-will employment:**
- Implied contracts (promises of job security in employee handbook or interviews)
- Public policy exceptions
- Good faith and fair dealing exceptions in some states

**What this means for you:**
- Document everything
- Keep records of performance reviews
- Know your rights before resigning`
      },
      {
        id: 'wage-and-hour',
        title: 'Wage and Hour Rights',
        content: `Federal and state laws establish minimum wage, overtime pay, and recordkeeping requirements.

**Minimum wage:**
- Federal minimum wage is $7.25/hour (many states and cities have higher)
- Tipped employees may have a lower base wage
- Some states require overtime after 8 hours in a day

**Overtime pay:**
- Non-exempt employees must receive 1.5x pay for hours over 40/week
- Some states have daily overtime rules
- "Exempt" employees (salaried, certain job duties) aren't entitled to overtime

**What employers must provide:**
- Accurate pay stubs or records
- Final paycheck within required timeframe
- Proper calculation of overtime

**Red flags to watch for:**
- Off-the-clock work requirements
- Misclassifying employees as contractors
- Illegal deductions from pay`
      },
      {
        id: 'discrimination-protection',
        title: 'Discrimination and Harassment Protections',
        content: `Federal and state laws protect employees from discrimination based on protected characteristics.

**Protected characteristics under federal law:**
- Race, color, national origin
- Sex, gender identity, sexual orientation
- Religion
- Age (40 and older)
- Disability
- Genetic information

**Types of illegal discrimination:**
- Hiring and firing decisions
- Pay and benefits
- Job assignments and promotions
- Training and apprenticeships
- Layoffs and recalls

**Harassment protections:**
- Quid pro quo harassment (job benefits for sexual favors)
- Hostile work environment
- Retaliation for complaining is illegal

**What to do:**
- Report to HR or management
- File with EEOC or state agency
- Document incidents with dates and details`
      },
      {
        id: 'family-medical-leave',
        title: 'Family and Medical Leave',
        content: `The Family and Medical Leave Act (FMLA) provides eligible employees with unpaid, job-protected leave for specific family and medical reasons.

**Eligibility:**
- Work for a covered employer (50+ employees within 75 miles)
- Have worked at least 12 months
- Have worked 1,250 hours in the past 12 months

**Reasons for leave:**
- Birth and care of a newborn child
- Adoption or foster care placement
- Care for an immediate family member with serious health condition
- Your own serious health condition

**FMLA benefits:**
- Up to 12 weeks of unpaid leave per year
- Continuation of health insurance
- Return to same or equivalent job
- Protection from retaliation`

      },
    ],
    faqs: [
      {
        question: 'Can my boss fire me for no reason?',
        answer: 'In at-will employment states, yes, you can be fired for any reason or no reason at all. However, you cannot be fired for discriminatory reasons, in retaliation for protected complaints, or in violation of an employment contract. About 12% of employees are in states with exceptions to at-will employment.'
      },
      {
        question: 'Am I entitled to overtime pay?',
        answer: 'If you are a non-exempt employee under the Fair Labor Standards Act, you must receive 1.5x your regular rate for hours worked over 40 in a workweek. Exempt employees (typically salaried workers in administrative, professional, or executive roles) are not entitled to overtime.'
      },
      {
        question: 'What should I do if I\'m being sexually harassed at work?',
        answer: 'Document every incident with details (who, what, when, where). Report it to HR or management in writing. File a charge with the EEOC within 180-300 days. Consider consulting an employment attorney. You cannot be retaliated against for reporting harassment.'
      },
      {
        question: 'Can my employer force me to work overtime?',
        answer: 'For non-exempt employees, yes, employers can require overtime. However, they must pay overtime rates and comply with daily hour limits in some states. Some states have meal and rest break requirements. Refusing overtime may be grounds for termination (unless for a protected reason).'
      },
      {
        question: 'Can I take time off for family medical leave?',
        answer: "You may qualify for FMLA leave if you've worked for a covered employer for 12+ months, 1,250+ hours, and there are 50+ employees within 75 miles. FMLA provides up to 12 weeks unpaid, job-protected leave for your own or family member's serious health condition, or for birth/adoption."
      },
    ],
    relatedTopics: [
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Unpaid wage claims' },
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Employment agreements' },
      { title: 'Personal Injury Basics', url: '/legal/personal-injury-basics', description: 'Workplace injuries' },
    ]
  },
  'family-law-questions': {
    title: 'Family Law Questions',
    description: 'Navigate divorce, custody, child support, and other family legal matters. Learn about your rights and responsibilities in family court.',
    keywords: ['divorce', 'child custody', 'child support', 'alimony', 'prenuptial agreement', 'adoption', 'guardianship'],
    quickAnswer: "Family law covers marriage, divorce, child custody, and support matters. Courts prioritize the best interests of children. Most divorce requires residency requirements and a waiting period. Custody decisions consider multiple factors including the child's relationship with each parent.",
    sections: [
      {
        id: 'divorce-basics',
        title: 'Understanding Divorce',
        content: `Divorce (dissolution of marriage) ends a marriage legally. There are different types of divorce in various states.

**Types of divorce:**
- **No-fault divorce:** No need to prove wrongdoing; marriage is irretrievably broken
- **Fault-based divorce:** Proving adultery, cruelty, abandonment, etc. (affects property division in some states)
- **Uncontested divorce:** Both spouses agree on all terms
- **Contested divorce:** Disputes over property, custody, or support

**General requirements:**
- Residency requirements (typically 6 months to 1 year in state)
- Filing a petition with the court
- Serving papers on spouse
- Waiting period (ranges from 90 days to over a year)

**What gets divided:**
- Marital property (assets/debts acquired during marriage)
- Retirement accounts (with QDRO)
- Sometimes includes debts`
      },
      {
        id: 'child-custody',
        title: 'Child Custody Basics',
        content: `Custody determines where children live and who makes major decisions about their welfare.

**Types of custody:**
- **Physical custody:** Where the child lives
- **Legal custody:** Decision-making power (education, healthcare, religion)

**Custody arrangements:**
- **Sole custody:** One parent has primary physical/legal custody
- **Joint custody:** Both parents share time and/or decision-making
- **Sole legal vs. joint legal** can differ from physical arrangements

**Best interest factors (courts consider):**
- Child\'s age and needs
- Each parent\'s ability to provide
- Child\'s relationship with each parent
- Stability of home environment
- Child\'s adjustment to home, school, community
- Each parent\'s mental and physical health
- Child\'s reasonable preference (usually 12+)

**Relocation issues:**
- Moving with children often requires court approval
- Non-custodial parents typically have visitation rights`
      },
      {
        id: 'child-support',
        title: 'Child Support',
        content: `Both parents have a legal obligation to financially support their children. Child support is based on income and time-sharing.

**How support is calculated:**
- Most states use income shares model
- Both parents\' incomes considered
- Percentage of income formula in some states
- Time-sharing affects calculation

**What support covers:**
- Food, clothing, shelter
- Education expenses
- Medical care and insurance
- Childcare costs
- Transportation for custody exchanges

**Modifications:**
- Can be modified if circumstances change significantly
- Must show substantial change (job loss, raise, relocation)
- Usually requires filing with the court

**Enforcement:**
- Wage garnishment common
- License suspension possible
- Tax refund interception
- Contempt of court if not paid`
      },
      {
        id: 'alimony-spousal-support',
        title: 'Alimony and Spousal Support',
        content: `Alimony (spousal support) is payments from one spouse to another after divorce. Not always awarded.

**Types of alimony:**
- **Rehabilitative:** Short-term support while recipient gets training/education
- **Permanent:** Long-term support (usually for long marriages)
- **Bridge:** Short-term, given during divorce proceedings
- **Lump sum:** One-time payment instead of ongoing

**Factors courts consider:**
- Length of marriage
- Age and health of both parties
- Earning capacity and work history
- Standard of living during marriage
- Contributions to marriage (homemaker, supporting spouse\'s career)
- Time needed for education/training

**Tax considerations:**
- Alimony is taxable income for recipient
- Deductible for payer (for divorces before 2019)
- Post-2019 divorces: no tax deduction`
      },
    ],
    faqs: [
      {
        question: 'How long does divorce take?',
        answer: "It depends on your state and whether it's contested. Uncontested divorces may take 3-6 months. Contested divorces can take 12-24 months or longer. Some states have mandatory waiting periods from 90 days to over a year."
      },
      {
        question: 'Can I get custody of my children if I\'m not married to the other parent?',
        answer: "Yes. If you're the legal father (through paternity establishment or being on birth certificate), you have custody rights. If unmarried, you may need to establish paternity first. Custody is determined based on the child's best interests."
      },
      {
        question: 'How is child support calculated?',
        answer: 'Most states use an income shares model that considers both parents\' incomes and the amount of time each parent spends with the child. Each state has specific guidelines and calculators. The court can deviate from guidelines in special circumstances.'
      },
      {
        question: 'Can I move to another state with my children after divorce?',
        answer: "Relocating with children usually requires either the other parent's consent or court approval. Courts consider whether the move is in the child's best interests and how it affects the non-custodial parent's visitation. Some custody orders include relocation clauses."
      },
      {
        question: 'Do I need a lawyer for family court?',
        answer: 'You\'re not required to have a lawyer, but family law can be complex and emotional. An attorney can help navigate procedures, protect your rights, and negotiate better outcomes. Many courts have self-help resources for unrepresented parties.'
      },
    ],
    relatedTopics: [
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Disputes under limits' },
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Prenuptial agreements' },
      { title: 'Debt Collection Rights', url: '/legal/debt-collection-rights', description: 'Support enforcement' },
    ]
  },
  'traffic-violations': {
    title: 'Traffic Violations and Tickets',
    description: 'Understand your options when you get a traffic ticket. Learn about points, insurance impact, traffic school, and fighting your ticket.',
    keywords: ['speeding ticket', 'traffic court', 'points on license', 'DUI', 'traffic school', 'license suspension', 'reckless driving'],
    quickAnswer: 'When you get a traffic ticket, you typically have three options: pay the fine and accept points, contest the ticket in court, or complete traffic school (if eligible). Points affect your insurance rates and can lead to license suspension if too many accumulate.',
    sections: [
      {
        id: 'understanding-tickets',
        title: 'Understanding Your Traffic Ticket',
        content: `A traffic ticket is a notice that you violated traffic laws. It typically includes: the violation, date/time/location, fine amount, and deadline to respond.

**Types of violations:**
- **Infraction:** Minor violations (speeding, running red light). Usually fines only, no jail.
- **Misdemeanor:** More serious (reckless driving, hit and run). Can have jail time.
- **Felony:** Serious crimes (DUI with injury, vehicular manslaughter).

**What the ticket tells you:**
- The specific violation code
- Fine amount (may have different payment options)
- Deadline to respond (usually 30 days)
- Whether you must appear in court or can just pay
- How to contest the ticket

**Important:**
- Don't ignore a ticket - failure to respond can lead to license suspension, warrant, or additional fines`
      },
      {
        id: 'points-insurance',
        title: 'Points, Insurance, and License Consequences',
        content: `Most states use a point system to track traffic violations. Accumulating too many points leads to consequences.

**How points work:**
- Each violation carries a point value (1-4 typically)
- Minor infractions (parking, broken light): 1 point
- Moderate (speeding 10-15 over): 2-3 points
- Serious (reckless driving, DUI): 4+ points
- Points typically stay on record 1-3 years

**Insurance impact:**
- Insurance companies check driving record
- Points can increase premiums 20-50% or more
- SR-22 filing may be required for serious violations
- Some violations make you high-risk

**License suspension thresholds:**
- Varies by state (typically 8-12 points in 12 months)
- Some violations automatically suspend (DUI, too many points)
- Suspension periods vary

**Reducing impact:**
- Complete traffic school (if eligible)
- Defensive driving courses
- Contest the ticket`
      },
      {
        id: 'fighting-ticket',
        title: 'Fighting Your Traffic Ticket',
        content: `You have the right to contest any traffic ticket. Going to court doesn't mean you're hiring a lawyer - you can represent yourself.

**Steps to contest:**
1. Request a court date (usually by the deadline on ticket)
2. Gather evidence (photos, dashcam footage, witness info)
3. Know the law (what elements must be proven)
4. Prepare your defense
5. Attend your hearing

**Possible defenses:**
- Officer wasn't present or didn't see the violation
- Equipment malfunction (speedometer, traffic light)
- Emergency situation
- Incorrect information on ticket
- Identity error

**Traffic court tips:**
- Dress professionally
- Be respectful to the judge
- Present facts, not excuses
- Request the officer's notes
- Ask to see evidence (radar/laser calibration records)

**Outcomes if you win:**
- Ticket dismissed
- Reduced violation (to non-point offense)`
      },
      {
        id: 'traffic-school',
        title: 'Traffic School and Diversion Programs',
        content: `Traffic school allows you to keep points off your record by completing a course. Diversion programs can result in ticket dismissal.

**Traffic school eligibility:**
- Varies by state and violation
- Usually available for minor infractions
- May be limited to once per year or 18 months
- Not available for serious violations (DUI, reckless)

**Benefits:**
- Points don't go on your record
- Insurance rates may not increase
- May keep CDL holders' records clean

**Types of courses:**
- In-person classes (typically 4-8 hours)
- Online courses (convenient, state-approved)
- Some courts require specific courses

**Costs:**
- Course fees (usually $20-$100)
- May have administrative fees
- Often cheaper than insurance increase

**Diversion programs:**
- First-time offender programs
- Plea deferment (plead guilty, complete program, charge dismissed)
- May require traffic school, essays, community service`
      },
    ],
    faqs: [
      {
        question: 'Will a speeding ticket raise my insurance?',
        answer: 'Usually, yes. Insurance companies check your driving record when setting rates. One minor speeding ticket may increase premiums 10-20%. Multiple violations or serious speeding (20+ over) can increase rates 50% or more. Check with your insurance agent about specific impacts.'
      },
      {
        question: 'Can I just ignore a traffic ticket?',
        answer: 'No. Ignoring a ticket can result in license suspension, additional fines, a warrant for your arrest, or the suspension becoming permanent. Always respond to a ticket by the deadline, even if you plan to pay it.'
      },
      {
        question: 'What happens if I don\'t pay a traffic ticket?',
        answer: 'Failure to pay can lead to license suspension, warrant for your arrest, registration hold, collection agency involvement, and additional fees. Some states may also suspend your ability to renew registration.'
      },
      {
        question: 'Should I hire a lawyer for a traffic ticket?',
        answer: 'For minor infractions, you may not need a lawyer. For serious violations (reckless driving, DUI), a lawyer is highly recommended. Lawyers can often negotiate reduced charges or represent you in court, potentially saving you more in fines and insurance than their fee.'
      },
      {
        question: 'How do I know if traffic school will work for me?',
        answer: "Check with your local court or DMV about eligibility. Most states allow traffic school for minor violations once every 12-18 months. It's not available for serious violations, commercial driver's license holders in some cases, or if you were driving over 100 mph."
      },
    ],
    relatedTopics: [
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Contesting tickets' },
      { title: 'Personal Injury Basics', url: '/legal/personal-injury-basics', description: 'Car accident rights' },
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Insurance claims' },
    ]
  },
  'small-claims-court': {
    title: 'Small Claims Court Guide',
    description: 'Learn how to sue in small claims court. Understand limits, procedures, and how to represent yourself effectively.',
    keywords: ['small claims court', 'how to sue', 'filing a lawsuit', 'civil court', 'money claim', 'serve papers', 'defendant'],
    quickAnswer: 'Small claims court is designed for people to represent themselves in disputes involving relatively small amounts of money. Limits range from $3,000 to $25,000 depending on the state. The process is simplified with less formal procedures and faster timelines.',
    sections: [
      {
        id: 'small-claims-basics',
        title: 'What is Small Claims Court?',
        content: `Small claims court is a special civil court designed to handle disputes involving smaller amounts of money quickly and inexpensively.

**Key features:**
- Simplified procedures (no complex legal forms)
- Usually no lawyers required (though allowed in some states)
- Faster than regular civil court
- Lower filing fees
- Limited discovery (evidence exchange)
- Appeals possible but limited

**Typical cases:**
- Unpaid debts
- Security deposit disputes
- Unreturned borrowed items
- Car repair problems
- Unpaid contractor work
- Property damage
- Personal loans
- Landlord disputes

**What small claims court CAN do:**
- Order payment of money
- Order return of specific property (in some states)
- Issue judgments you can enforce

**What small claims court CANNOT do:**
- Grant divorces or custody
- Handle criminal matters
- Award more than the jurisdictional limit`
      },
      {
        id: 'jurisdiction-limits',
        title: 'Understanding Jurisdiction and Limits',
        content: `Each state sets a maximum amount you can sue for in small claims court. This is called the jurisdictional limit.

**State limits (examples):**
- California: $12,500
- New York: $5,000
- Texas: $20,000
- Florida: $8,000
- Washington: $10,000
- Many states: $5,000-$7,500

**Finding your state's limit:**
- Contact your local small claims court
- Check online at court website
- Small amounts are typically under $10,000

**What if your claim is larger?**
- Sue for the maximum amount
- Consider if you can split the claim
- File in regular civil court (higher fees, lawyer usually needed)
- Use mediation or arbitration

**Where to file:**
- Usually in the county where:
  - The defendant lives
  - The incident occurred
  - The contract was signed`
      },
      {
        id: 'filing-process',
        title: 'How to File a Small Claims Case',
        content: `The filing process involves several steps. Here's what to expect:

**Step 1: Gather information**
- Your information (name, address, phone)
- Defendant's information (name, address, phone)
- Incident details and dates
- Evidence (contracts, photos, receipts, witnesses)
- Amount you're claiming

**Step 2: Complete forms**
- Small claims complaint form (available at court or online)
- Include filing fee (typically $30-$100)
- May need additional forms depending on jurisdiction

**Step 3: File with the court**
- Take forms to the small claims court clerk
- Pay the filing fee
- Get a case number and hearing date

**Step 4: Serve the defendant**
- Have papers served on defendant
- Use process server, sheriff, or certified mail
- Must be served within a certain timeframe
- Get proof of service to file with court

**Step 5: Prepare for trial**
- Gather all evidence
- Prepare your presentation
- Notify witnesses`
      },
      {
        id: 'trial-preparation',
        title: 'Preparing for Your Small Claims Trial',
        content: `Winning in small claims court requires good preparation. Here's how to build your case:

**Organize your evidence:**
- Contracts, agreements, receipts
- Photos and videos
- Correspondence (emails, texts, letters)
- Witness information
- Repair estimates or invoices
- Bank statements

**What to bring to trial:**
- All evidence organized clearly
- Original documents (copies for court)
- Timeline of events
- Written statement of what happened
- Calculator for any money amounts

**Presenting your case:**
- Be brief and organized
- Stick to facts, not emotions
- Present evidence in logical order
- Have witnesses testify about what they saw
- Be respectful to the judge

**Defendant's possible defenses:**
- You were paid (bring receipts)
- Item was returned
- Contract terms you violated
- Statute of limitations expired
- You caused the damage

**After judgment:**
- If you win, you still need to collect
- If you lose, you may be able to appeal`
      },
    ],
    faqs: [
      {
        question: 'How much can I sue for in small claims court?',
        answer: "Limits vary by state, typically ranging from $3,000 to $25,000. Most states are between $5,000 and $10,000. Check your specific state's limit by contacting the small claims court or checking online."
      },
      {
        question: 'Do I need a lawyer for small claims court?',
        answer: 'No, small claims court is designed for people to represent themselves. Lawyers are usually not allowed or not practical given the small amounts involved. However, you can consult with a lawyer before trial if needed.'
      },
      {
        question: 'How long does small claims court take?',
        answer: 'From filing to trial, typically 30-90 days. The hearing itself usually takes 30-60 minutes. Collection after winning can take much longer if the defendant doesn\'t pay.'
      },
      {
        question: 'Can I sue someone who lives in a different state?',
        answer: "Yes, but you'll need to follow that state's laws for serving papers. You may need to hire a process server in that state. Consider if it's worth the extra cost and complexity."
      },
      {
        question: 'What if the person doesn\'t pay after I win?',
        answer: 'You can\'t force payment directly, but you can: request wage garnishment, place a lien on property, levy bank accounts, or report to credit agencies. These enforcement actions require additional court procedures.'
      },
    ],
    relatedTopics: [
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Contract disputes' },
      { title: 'Landlord Tenant Rights', url: '/legal/landlord-tenant-rights', description: 'Deposit disputes' },
      { title: 'Debt Collection Rights', url: '/legal/debt-collection-rights', description: 'Unpaid debts' },
    ]
  },
  'contract-questions': {
    title: 'Contract Law Basics',
    description: 'Understand contracts, agreements, and your legal rights. Learn what makes a contract valid and how to handle contract disputes.',
    keywords: ['contract law', 'valid contract', 'breach of contract', 'contract dispute', 'verbal agreement', 'written contract', 'signing a contract'],
    quickAnswer: 'A contract is a legally binding agreement. For a contract to be valid, it needs offer, acceptance, consideration, capacity, and legality. Written contracts are easier to enforce, but many verbal agreements are also legally binding.',
    sections: [
      {
        id: 'contract-elements',
        title: 'Elements of a Valid Contract',
        content: `For a contract to be legally enforceable, it typically needs these five elements:

**1. Offer:**
- One party makes a promise to do or not do something
- Must be definite and communicated to the other party
- Examples: "I'll sell you my car for $5,000"

**2. Acceptance:**
- The other party agrees to the terms
- Must be unconditional and match the offer exactly
- Can be written, verbal, or implied by conduct

**3. Consideration:**
- Something of value exchanged between parties
- Money, goods, services, or promises
- Both sides must give something

**4. Capacity:**
- Parties must be legally capable of contracting
- Usually means being of legal age and sound mind
- Minors and mentally incapacitated persons can void contracts

**5. Legality:**
- Contract must be for a legal purpose
- Illegal contracts (like contracts to commit crimes) are void

**What makes a contract void or voidable:**
- Fraud or misrepresentation
- Duress or undue influence
- Mistake
- Illegality
- Lack of capacity`
      },
      {
        id: 'written-vs-verbal',
        title: 'Written vs. Verbal Contracts',
        content: `Both written and verbal contracts can be legally binding, but there are important differences.

**Verbal contracts:**
- Can be legally enforceable
- Easier to make but harder to prove
- Many contracts are valid when verbal
- Statute of frauds requires some contracts be written:
  - Real estate transactions
  - Contracts taking more than one year to perform
  - Sale of goods over $500 (UCC)
  - Surety agreements
  - Marriage promises

**Written contracts:**
- Clearer evidence of terms
- Easier to prove in court
- Often required by law for larger transactions
- May include additional protections (warranties)

**Best practices:**
- Get important agreements in writing
- Keep copies of all signed contracts
- Ensure all pages are initialed if initial required
- Read everything before signing
- Don't sign blank forms or incomplete documents`
      },
      {
        id: 'breach-of-contract',
        title: 'Understanding Breach of Contract',
        content: `A breach occurs when one party fails to fulfill their obligations under the contract.

**Types of breaches:**
- **Minor breach:** Substantial performance, may owe partial damages
- **Material breach:** Major failure, other party excused from performance
- **Anticipatory breach:** Party indicates they won't perform before due date

**Remedies for breach:**
- **Damages:** Money to compensate for loss
  - Compensatory (direct losses)
  - Consequential (foreseeable indirect losses)
  - Liquidated (specified in contract)
  - Punitive (rare in contract cases)
- **Specific performance:** Court orders party to perform
- **Rescission:** Contract canceled, parties restored to pre-contract position
- **Restitution:** Return of any benefit conferred

**Steps to take:**
1. Review the contract
2. Document the breach
3. Send formal demand letter
4. Consider mediation or arbitration
5. File lawsuit if necessary`
      },
      {
        id: 'common-contracts',
        title: 'Common Contract Types',
        content: `Here are some contracts you might encounter in daily life:

**Employment contracts:**
- Can be written or verbal
- May include non-compete, non-disclosure clauses
- At-will employment means either party can end it

**Lease agreements:**
- Govern landlord-tenant relationships
- Include rent, duration, and rules
- Governed by state landlord-tenant law

**Service contracts:**
- For home repairs, maintenance, professional services
- Should specify scope, timeline, and cost
- May include warranties

**Sales contracts:**
- For buying goods or property
- Uniform Commercial Code (UCC) governs goods
- Real estate has special requirements

**Tips for signing:**
- Read everything before signing
- Don't feel pressured to sign immediately
- Ask questions about unclear terms
- Get promises in writing
- Keep a copy`
      },
    ],
    faqs: [
      {
        question: 'Is a verbal agreement legally binding?',
        answer: 'Yes, many verbal agreements are legally binding. However, some contracts must be in writing under the statute of frauds (real estate, goods over $500, contracts taking more than a year). Written contracts are always better for evidence.'
      },
      {
        question: 'Can I get out of a contract I signed?',
        answer: 'You may be able to cancel a contract if: there was fraud or misrepresentation, you were pressured into signing, the other party breached first, you have a cooling-off period (some contracts), or the contract is illegal. Many contracts have cancellation clauses.'
      },
      {
        question: 'What should I do if someone breaches a contract with me?',
        answer: 'First, review the contract to understand obligations. Document the breach with dates and details. Send a formal demand letter outlining the problem and desired resolution. Consider mediation. If unresolved, you may sue for damages or specific performance.'
      },
      {
        question: 'Do I need a lawyer to review a contract?',
        answer: 'Not legally required, but advisable for important contracts (business, real estate, significant amounts). For routine personal agreements, reading carefully and asking questions is often sufficient. Many lawyers offer brief contract reviews.'
      },
      {
        question: 'What is the statute of limitations on contract disputes?',
        answer: "Varies by state and contract type, typically 2-6 years for written contracts and 1-4 years for verbal contracts. Check your state's specific limitations. Time begins when the breach occurred or was discovered."
      },
    ],
    relatedTopics: [
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Contract disputes' },
      { title: 'Consumer Rights', url: '/legal/consumer-rights', description: 'Warranty claims' },
      { title: 'Employee Rights', url: '/legal/employee-rights', description: 'Employment agreements' },
    ]
  },
  'personal-injury-basics': {
    title: 'Personal Injury Basics',
    description: 'Understand your rights after an injury. Learn about negligence, compensation, and when to consider legal action.',
    keywords: ['personal injury', 'negligence', 'car accident', 'slip and fall', 'compensation', 'pain and suffering', 'liability'],
    quickAnswer: "Personal injury law allows you to seek compensation when someone else's negligence causes you harm. You may be entitled to economic damages (medical costs, lost wages) and non-economic damages (pain and suffering). Most cases settle before trial.",
    sections: [
      {
        id: 'negligence-basics',
        title: 'Understanding Negligence',
        content: `Negligence is the foundation of most personal injury cases. To prove negligence, you need four elements:

**1. Duty of care:**
- The defendant owed you a legal duty to act reasonably
- Example: Drivers must follow traffic laws
- Example: Property owners must maintain safe conditions

**2. Breach of duty:**
- The defendant failed to meet that duty
- Did not act as a reasonable person would
- Shortcuts, carelessness, or intentional harm

**3. Causation:**
- The breach caused your injury
- "But for" test: But for the breach, injury wouldn't have occurred
- Proximate cause: Injury was foreseeable result

**4. Damages:**
- You suffered actual harm or loss
- Medical bills, lost wages, pain, property damage

**Types of negligence:**
- **Comparative negligence:** Your recovery reduced by your percentage of fault
- **Contributory negligence:** In a few states, any fault bars recovery
- **Gross negligence:** Willful or reckless conduct (may allow punitive damages)`

      },
      {
        id: 'types-of-claims',
        title: 'Types of Personal Injury Claims',
        content: `Personal injury covers many situations. Here are common types:

**Car accidents:**
- Most common personal injury claims
- Claims against other driver or insurance
- Includes property damage and injuries

**Slip and fall:**
- Property liability claims
- Owner must have known or should have known of danger
- Conditions like wet floors, uneven surfaces, poor lighting

**Medical malpractice:**
- Healthcare provider negligence
- Must prove standard of care was violated
- Often requires expert testimony

**Product liability:**
- Defective products causing injury
- Manufacturing, design, or warning defects
- Can sue manufacturer, seller, or all involved

**Workplace injuries:**
- Usually covered by workers' compensation
- No need to prove negligence
- Exclusive remedy in most cases

**Assault and battery:**
- Intentional tort, not negligence
- May have criminal and civil components
- Can sue for damages`

      },
      {
        id: 'damages-compensation',
        title: 'Understanding Damages and Compensation',
        content: `Damages are what you can recover for your injuries. They come in different types:

**Economic damages (objective):**
- Medical expenses (past and future)
- Lost wages and earning capacity
- Property damage
- Rehabilitation costs
- Home modifications if needed

**Non-economic damages (subjective):**
- Pain and suffering
- Mental anguish
- Loss of enjoyment of life
- Loss of consortium (relationship impact)
- Emotional distress

**Punitive damages:**
- Awarded to punish egregious conduct
- Not based on your losses
- Requires showing of malice or gross negligence
- Not available in all cases
- Often capped by state law

**Calculating damages:**
- Medical bills provide documentation
- Pain and suffering uses multiplier (1-5x economic damages)
- Lost wages need proof (pay stubs, doctor notes)
- Future damages require expert testimony`

      },
      {
        id: 'statute-of-limitations',
        title: 'Statute of Limitations for Personal Injury',
        content: `The statute of limitations sets the deadline for filing a personal injury lawsuit. After this time, your claim is barred.

**Typical timeframes:**
- Most states: 2-3 years for personal injury
- Some states: 1 year (Kentucky, Louisiana)
- Some states: 6 years (Maine)
- Wrongful death: often same or shorter

**When the clock starts:**
- Date of injury in most cases
- Date injury was discovered (discovery rule)
- Date of death for wrongful death

**Special considerations:**
- Minors may have extended time
- Mentally incapacitated persons
- Claims against government have shorter deadlines
- Must follow specific procedures for government claims

**What to do:**
- Don't wait to start your claim
- Gather evidence while fresh
- Document injuries with photos
- Keep all medical records
- Consult attorney early`

      },
    ],
    faqs: [
      {
        question: 'How long do I have to file a personal injury lawsuit?',
        answer: "Most states give 2-3 years from the date of injury. Some states allow 1-6 years. It's critical to file before the statute of limitations expires. Claims against government entities often have much shorter deadlines (as short as 6 months).'"
      },
      {
        question: 'How much is my personal injury case worth?',
        answer: 'Value depends on: medical expenses, lost wages, pain and suffering, future damages, strength of evidence, and insurance limits. Minor injuries may settle for a few thousand dollars. Serious injuries can reach six or seven figures. An attorney can give you a better estimate.'
      },
      {
        question: 'Do I need a lawyer for a personal injury case?',
        answer: 'For minor cases with clear liability, you may handle yourself. For serious injuries, disputed liability, or insurance company resistance, an attorney is valuable. Most personal injury attorneys work on contingency (take percentage of recovery, no fee if no recovery).'
      },
      {
        question: 'Will my personal injury case go to trial?',
        answer: 'Most personal injury cases settle before trial, often during or after mediation. Only about 5-10% of cases go to trial. Trials are unpredictable and expensive, so both sides usually prefer settlement.'
      },
      {
        question: 'What if I was partly at fault for my injury?',
        answer: 'It depends on your state. In comparative negligence states, your recovery is reduced by your percentage of fault (e.g., 20% fault means 80% recovery). In a few states (AL, MD, NC, VA, DC), any fault bars recovery. Some states have modified comparative negligence.'
      },
    ],
    relatedTopics: [
      { title: 'Traffic Violations', url: '/legal/traffic-violations', description: 'Car accidents' },
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Minor injury claims' },
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Insurance claims' },
    ]
  },
  'debt-collection-rights': {
    title: 'Debt Collection Rights',
    description: 'Know your rights when dealing with debt collectors. Learn about the Fair Debt Collection Practices Act and how to handle collection calls.',
    keywords: ['debt collection', 'FDCPA', 'harassment', 'debt collector', 'collection calls', 'statute of limitations', 'credit report'],
    quickAnswer: 'The Fair Debt Collection Practices Act (FDCPA) protects you from abusive debt collection practices. Debt collectors cannot harass you, lie, or use unfair practices. You have the right to dispute debts and request validation.',
    sections: [
      {
        id: 'fdcpa-overview',
        title: 'Your Rights Under the FDCPA',
        content: `The Fair Debt Collection Practices Act (FDCPA) is a federal law that regulates how debt collectors can interact with you.

**Who the FDCPA protects:**
- Consumers (individuals)
- Not businesses
- Only applies to third-party debt collectors
- Original creditors have more flexibility

**Debt collectors CANNOT:**
- Call before 8am or after 9pm (your time zone)
- Call you at work if you've told them to stop
- Harass or abuse (threats, profanity, repeated calls)
- Lie about the debt amount or consequences
- Threaten actions they can't or won't take
- Deposit post-dated checks early
- Use deceptive practices

**Debt collectors MUST:**
- Validate the debt if you request
- Cease contact if you send written request
- Provide the name of the original creditor
- Provide amount owed and creditor information
- Stop calling after you've sent dispute letter

**Enforcement:**
- File complaint with CFPB or FTC
- Sue for violations within one year
- May recover damages and attorney's fees`
      },
      {
        id: 'disputing-debt',
        title: 'How to Dispute a Debt',
        content: `You have the right to dispute a debt and request validation from the debt collector.

**Within 30 days of initial contact:**
- Send a written dispute letter
- Certified mail with return receipt
- Request validation (proof of the debt)
- Request the name of the original creditor
- Ask for verification of the amount

**What happens after you dispute:**
- Debt collector must stop collection efforts
- They cannot call or send letters about the debt
- They must obtain verification of the debt
- If they can't verify, they must cease collection

**Sample dispute letter:**
"Your letter dated [date] indicates I owe [amount]. I dispute this debt and request validation. I request that you provide: the amount of the debt, the name of the original creditor, and documentation proving I owe this debt. Please cease all collection efforts until you provide this validation."

**Keep copies:**
- Keep copies of all correspondence
- Document all calls (date, time, what was said)
- Save envelopes and return receipts`
      },
      {
        id: 'statute-limitations',
        title: 'Statute of Limitations on Debt',
        content: `Each state sets a time limit for collectors to sue you on a debt. After this time, the debt is "time-barred."

**Common time limits:**
- Written contracts: 4-6 years in most states
- Oral agreements: 3-6 years
- Credit card debt: 3-10 years
- Medical debt: Varies

**What this means:**
- Collectors can still ask for payment
- They cannot sue you in court
- If sued, you can use statute of limitations as defense
- Debt doesn't disappear from credit report

**Important notes:**
- Time may restart if you acknowledge the debt
- Making a payment can restart the clock
- Verbal acknowledgment may restart it
- Written acknowledgment definitely restarts it

**On your credit report:**
- Most negative items fall off after 7 years
- Bankruptcies stay 7-10 years
- Time-barred debts can still be reported`

      },
      {
        id: 'stopping-harassment',
        title: 'Stopping Debt Collector Harassment',
        content: `If debt collectors are harassing you, there are steps you can take.

**Document everything:**
- Keep log of all calls
- Record calls if legal in your state (one-party consent states)
- Save all letters and emails
- Note threats and misleading statements

**Immediate actions:**
- Send written request to stop calls
- Send cease and desist letter
- Request validation of debt
- Report violations

**Reporting violations:**
- Consumer Financial Protection Bureau (CFPB)
- Federal Trade Commission (FTC)
- Your state attorney general
- Local consumer protection agency

**If sued:**
- Don't ignore it - response deadline is critical
- File answer by the deadline (usually 20-30 days)
- Assert statute of limitations defense
- Consider consulting an attorney

**Remember:**
- You have rights
- Document everything
- Act quickly on legal documents`
      },
    ],
    faqs: [
      {
        question: 'Can debt collectors call me at any time?',
        answer: 'No. The FDCPA restricts calls to between 8am and 9pm in your time zone. They cannot call you at work if you\'ve told them not to, and must stop calling if you send a written cease and desist request.'
      },
      {
        question: 'What should I do if a debt collector sues me?',
        answer: 'Don\'t ignore it! You typically have 20-30 days to respond. File a written answer with the court. Assert any defenses including statute of limitations. Consider consulting an attorney. A default judgment can lead to wage garnishment.'
      },
      {
        question: 'Can I be sued for old debt?',
        answer: 'Collectors can sue for old debts, but only if the statute of limitations hasn\'t passed. If the time has passed, you can use this as a defense. Be careful - acknowledging the debt or making a payment can restart the clock.'
      },
      {
        question: 'Does a debt collector have to validate the debt?',
        answer: 'Within 30 days of their initial letter, you can send a written dispute requesting validation. They must stop collection efforts until they provide verification. If they can\'t verify the debt, they must cease collection entirely.'
      },
      {
        question: 'How do I remove old debts from my credit report?',
        answer: 'You can dispute inaccurate information with credit bureaus. If the debt is accurately reported but old, it should fall off after 7 years. You can negotiate a "pay for delete" with collectors, but they\'re not required to agree.'
      },
    ],
    relatedTopics: [
      { title: 'Contract Questions', url: '/legal/contract-questions', description: 'Credit agreements' },
      { title: 'Small Claims Court', url: '/legal/small-claims-court', description: 'Debt disputes' },
      { title: 'Consumer Rights', url: '/legal/consumer-rights', description: 'Financial protections' },
    ]
  },
};

const categoryNavItems = [
  { category: 'landlord-tenant-rights', label: 'Landlord & Tenant', icon: '🏠' },
  { category: 'employee-rights', label: 'Employee Rights', icon: '💼' },
  { category: 'family-law-questions', label: 'Family Law', icon: '👨‍👩‍👧' },
  { category: 'traffic-violations', label: 'Traffic Tickets', icon: '🚗' },
  { category: 'small-claims-court', label: 'Small Claims', icon: '⚖️' },
  { category: 'contract-questions', label: 'Contracts', icon: '📝' },
  { category: 'personal-injury-basics', label: 'Personal Injury', icon: '🏥' },
  { category: 'debt-collection-rights', label: 'Debt Collection', icon: '💳' },
];

// SEO metadata generator
function generateSEO(category: string, content: typeof categoryContent[string]) {
  return {
    title: `${content.title} | Not Legal Advice - Free Legal Information`,
    description: content.description,
    keywords: content.keywords.join(', '),
    og: {
      title: content.title,
      description: content.description,
      type: 'article',
    },
    twitter: {
      card: 'summary_large_image',
      title: content.title,
      description: content.description,
    },
  };
}

// JSON-LD schema generator
function generateSchema(category: string, content: typeof categoryContent[string]) {
  const baseUrl = 'https://notlegal.chat';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title,
    description: content.description,
    keywords: content.keywords.join(', '),
    author: {
      '@type': 'Organization',
      name: 'notlegal.chat',
      url: baseUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'notlegal.chat',
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logo.png`,
      },
    },
    datePublished: '2026-01-01',
    dateModified: '2026-01-20',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${baseUrl}/legal/${category}`,
    },
    about: content.sections.map((s) => ({
      '@type': 'Thing',
      name: s.title,
      description: s.content.substring(0, 200),
    })),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return [articleSchema, faqSchema];
}

// Helper function to format category slug
function formatCategorySlug(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function LegalCategoryPage() {
  const { category } = useParams<{ category: string }>();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [showCta, setShowCta] = useState(false);

  // Scroll to top on mount and when location changes (for related topic clicks)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Get content for this category
  const content = category && categoryContent[category]
    ? categoryContent[category]
    : null;

  // Redirect or show 404 if category not found
  if (!content) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-surface-900 mb-4">Category Not Found</h1>
          <p className="text-surface-600 mb-6">The legal category you're looking for doesn't exist.</p>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const seo = generateSEO(category, content);
  const schemas = generateSchema(category, content);

  // Track scrolling for table of contents
  useEffect(() => {
    const handleScroll = () => {
      const sections = content.sections.map(s => s.id);
      for (const sectionId of sections.reverse()) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [content]);

  // Show CTA when scrolling past halfway
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollPosition > docHeight * 0.6 && !showCta) {
        setShowCta(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showCta]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAskQuestion = () => {
    window.location.href = '/chat';
  };

  return (
    <div className="min-h-screen bg-surface-50">
      {/* SEO Helmet */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <meta property="og:title" content={seo.og.title} />
        <meta property="og:description" content={seo.og.description} />
        <meta property="og:type" content={seo.og.type} />
        <meta name="twitter:card" content={seo.twitter.card} />
        <meta name="twitter:title" content={seo.twitter.title} />
        <meta name="twitter:description" content={seo.twitter.description} />
        <link rel="canonical" href={`https://notlegal.chat/legal/${category}`} />
      </Helmet>

      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }} />

      {/* Global Disclaimer Banner */}
      <DisclaimerBanner />

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-surface-200 bg-white/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <NotLegalIcon name="gavel" className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-surface-900">notlegal.chat</span>
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/pricing" className="text-sm font-medium text-surface-600 hover:text-primary-600 transition-colors">
              Pricing
            </Link>
            <Link to="/chat">
              <Button size="sm">Ask a Question</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-surface-100 border-b border-surface-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-surface-500 hover:text-primary-600 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-surface-400" />
            <Link to="/" className="text-surface-500 hover:text-primary-600 transition-colors">Legal Topics</Link>
            <ChevronRight className="w-4 h-4 text-surface-400" />
            <span className="text-surface-900 font-medium">{content.title}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar - Table of Contents */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">
              <Card variant="elevated" padding="md">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary-600" />
                    Contents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <nav className="space-y-1">
                    <button
                      onClick={() => scrollToSection('quick-answer')}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg text-surface-600 hover:bg-surface-100 hover:text-primary-600 transition-colors"
                    >
                      Quick Answer
                    </button>
                    {content.sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                          activeSection === section.id
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-surface-600 hover:bg-surface-100 hover:text-primary-600'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                    <button
                      onClick={() => scrollToSection('faq')}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg text-surface-600 hover:bg-surface-100 hover:text-primary-600 transition-colors"
                    >
                      FAQ
                    </button>
                    <button
                      onClick={() => scrollToSection('related-topics')}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg text-surface-600 hover:bg-surface-100 hover:text-primary-600 transition-colors"
                    >
                      Related Topics
                    </button>
                  </nav>
                </CardContent>
              </Card>

              {/* Quick Navigation */}
              <Card variant="outlined" padding="md" className="mt-4">
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-surface-600">
                    Browse Topics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {categoryNavItems.map((item) => (
                      <Link
                        key={item.category}
                        to={`/legal/${item.category}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          item.category === category
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-surface-600 hover:bg-surface-100'
                        }`}
                      >
                        <span>{item.icon}</span>
                        <span className="truncate">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="lg:col-span-9">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-surface-900 mb-4">
                {content.title}
              </h1>
              <p className="text-lg text-surface-600">
                {content.description}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {content.keywords.slice(0, 5).map((keyword) => (
                  <span
                    key={keyword}
                    className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Quick Answer Box - Featured Snippet */}
            <Card variant="elevated" padding="lg" id="quick-answer" className="mb-8 bg-gradient-to-br from-primary-50 to-white border-primary-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-surface-900 mb-2 flex items-center gap-2">
                    Quick Answer
                  </h2>
                  <p className="text-surface-700 leading-relaxed">
                    {content.quickAnswer}
                  </p>
                </div>
              </div>
            </Card>

            {/* Content Sections */}
            <div className="space-y-8">
              {content.sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card variant="elevated" padding="lg">
                    <h2 className="text-xl font-bold text-surface-900 mb-4 flex items-center gap-3">
                      <span className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 font-semibold text-sm">
                        {index + 1}
                      </span>
                      {section.title}
                    </h2>
                    <div className="prose prose-surface max-w-none text-surface-600 leading-relaxed">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-4">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
                          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold text-surface-900">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                        }}
                      >
                        {section.content}
                      </ReactMarkdown>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* FAQ Section */}
            <section id="faq" className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-surface-900">Frequently Asked Questions</h2>
                  <p className="text-surface-600">Common questions about {formatCategorySlug(category || '').toLowerCase()}</p>
                </div>
              </div>

              <div className="space-y-4">
                {content.faqs.map((faq, index) => (
                  <Card key={index} variant="outlined" padding="none">
                    <motion.div
                      initial={false}
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-surface-50 transition-colors"
                      >
                        <span className="font-medium text-surface-900 pr-4">
                          {faq.question}
                        </span>
                        <motion.span
                          animate={{ rotate: expandedFaq === index ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex-shrink-0 text-surface-400"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.span>
                      </button>
                      <AnimatePresence>
                        {expandedFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-4 text-surface-600 leading-relaxed border-t border-surface-100 pt-4">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </Card>
                ))}
              </div>
            </section>

            {/* Related Topics */}
            <section id="related-topics" className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                  <Scale className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-surface-900">Related Topics</h2>
                  <p className="text-surface-600">Explore more legal information</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {content.relatedTopics.map((topic, index) => (
                  <Link
                    key={index}
                    to={topic.url}
                    className="group block"
                  >
                    <Card
                      variant="outlined"
                      padding="md"
                      className="h-full hover:border-primary-300 hover:shadow-md transition-all"
                    >
                      <h3 className="font-semibold text-surface-900 group-hover:text-primary-600 transition-colors mb-2 flex items-center gap-2">
                        {topic.title}
                        <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </h3>
                      <p className="text-sm text-surface-500">{topic.description}</p>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <AnimatePresence>
              {showCta && (
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 40 }}
                  transition={{ duration: 0.4 }}
                  className="mt-12"
                >
                  <Card
                    variant="elevated"
                    padding="lg"
                    className="bg-gradient-to-r from-primary-600 to-primary-700 text-white text-center"
                  >
                    <div className="max-w-2xl mx-auto">
                      <h3 className="text-2xl font-bold mb-3">
                        Have a specific legal question?
                      </h3>
                      <p className="text-primary-100 mb-6">
                        Get personalized answers about your unique situation. Not legal advice, but close enough.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button
                          variant="secondary"
                          size="lg"
                          onClick={handleAskQuestion}
                          icon={<MessageCircle className="w-5 h-5" />}
                        >
                          Ask Your Question
                        </Button>
                        <Link to="/">
                          <Button
                            variant="ghost"
                            size="lg"
                            className="text-white hover:bg-white/10"
                          >
                            Explore More Topics
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Footer Disclaimer */}
      <footer className="bg-surface-900 py-8 px-6 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-surface-800 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-white mb-2">Important Disclaimer</h4>
                <p className="text-surface-400 text-sm leading-relaxed">
                  The information provided on this website is for general informational purposes only and does not constitute legal advice.
                  Laws vary by state and individual circumstances. This content is not intended to create an attorney-client relationship.
                  For actual legal advice, please consult with a licensed attorney in your jurisdiction.
                  Not legal advice. We are not lawyers. Please don't sue us.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2">
              <NotLegalIcon name="gavel" className="w-6 h-6 text-primary-500" />
              <span className="text-white font-semibold">notlegal.chat</span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-surface-400">
              <Link to="/disclaimer" className="hover:text-white transition-colors">Full Disclaimer</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/chat" className="hover:text-white transition-colors">Ask a Question</Link>
            </div>
            <p className="text-surface-500 text-sm">
              © 2026 notlegal.chat. Not a real company.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LegalCategoryPage;
